import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Filter, ChevronDown, Plus, Calendar } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useI18n } from "@/hooks/useI18n";
import { cn } from "@/components/utils/utils";
import { DateRange } from "react-day-picker";

export interface FilterConfig {
  field: string;
  operation: string;
  value: string;
  dateRange?: {
    from?: Date;
    to?: Date;
  };
}

export interface FilterOption {
  value: string;
  label: string;
  is_date?: boolean;
  operations: Array<{
    value: string;
    label: string;
  }>;
}

interface FilterDropdownProps {
  filters: FilterConfig[];
  onFiltersChange: (filters: FilterConfig[]) => void;
  options: FilterOption[];
  triggerClassName?: string;
  disabled?: boolean;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filters,
  onFiltersChange,
  options,
  triggerClassName = "",
  disabled = false,
}) => {
  const [filterForm, setFilterForm] = useState<FilterConfig>({
    field: '',
    operation: '',
    value: ''
  });
  const { t, language } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined
  });

  const formatDate = (date: Date) =>
    date.toLocaleDateString(language, { day: "2-digit", month: "short", year: "numeric" });

  const handleAddFilter = () => {
    const isValidFilter = filterForm.field && (
      isDateField(filterForm.field) 
        ? (dateRange.from || dateRange.to)
        : (filterForm.operation && filterForm.value)
    );
    
    if (isValidFilter) {
      const filterToAdd = { ...filterForm };
      
      // Se è un campo di data, aggiungi il range
      if (isDateField(filterForm.field)) {
        filterToAdd.dateRange = dateRange;
        filterToAdd.value = ''; // Per i campi di data non usiamo il value
      }
      
      onFiltersChange([...filters, filterToAdd]);
      setFilterForm({ field: '', operation: '', value: '' });
      setDateRange({ from: undefined, to: undefined });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isValidFilter = filterForm.field && (
      isDateField(filterForm.field) 
        ? (dateRange.from || dateRange.to)
        : (filterForm.operation && filterForm.value)
    );
    
    if (e.key === 'Enter' && isValidFilter) {
      handleAddFilter();
    }
  };

  const handleRemoveFilter = (index: number) => {
    onFiltersChange(filters.filter((_, i) => i !== index));
  };

  const getOperationsForField = (field: string) => {
    const option = options.find(opt => opt.value === field);
    return option?.operations || [];
  };

  const isDateField = (field: string) => {
    const option = options.find(opt => opt.value === field);
    return option?.is_date || false;
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className={`flex items-center gap-1 sm:gap-2 ${triggerClassName}`}
          disabled={disabled}
        >
          <Filter className="h-4 w-4" />
          <span className="hidden sm:inline">{t('ui.filterDropdown.trigger')}</span>
          {filters.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-sm bg-gray-900 text-white rounded min-w-[18px] flex items-center justify-center">
              {filters.length}
            </span>
          )}
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] max-w-[600px] p-4 mx-4">
        <div className="space-y-4">
          <h4 className="font-medium">{t('ui.filterDropdown.title')}</h4>

          {/* Form in linea */}
          <div className="flex flex-col sm:flex-row sm:space-y-0 space-y-3 items-end gap-3">
            {/* Mobile: Prima riga - Campo e Operazione */}
            {/* Desktop: Tutti i campi in una riga */}
            <div className="flex flex-wrap items-end gap-3 w-full sm:w-auto">
              {/* Campo */}
              <div className="flex-1 min-w-[200px] space-y-2">
                <label className="font-medium">{t('ui.filterDropdown.field')}</label>
                <Select
                  value={filterForm.field}
                  onValueChange={(value) => {
                    const fieldOption = options.find(opt => opt.value === value);
                    const operations = fieldOption?.operations || [];
                    
                    // Se è un campo di data, non serve l'operazione
                    if (fieldOption?.is_date) {
                      setFilterForm({ 
                        ...filterForm, 
                        field: value, 
                        operation: '', 
                        value: '' 
                      });
                    } else {
                      // Se c'è solo un'operazione disponibile, selezionala automaticamente
                      const autoOperation = operations.length === 1 ? operations[0].value : '';
                      setFilterForm({ 
                        ...filterForm, 
                        field: value, 
                        operation: autoOperation, 
                        value: '' 
                      });
                    }
                    setDateRange({ from: undefined, to: undefined });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('ui.selectField')} />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Operazione - nascosta per i campi di data */}
              {!isDateField(filterForm.field) && (
                <div className="flex-1 min-w-[150px] space-y-2">
                  <label className="font-medium">{t('ui.filterDropdown.operation')}</label>
                  <Select
                    value={filterForm.operation}
                    onValueChange={(value) => setFilterForm({ ...filterForm, operation: value, value: '' })}
                    disabled={!filterForm.field}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('ui.selectOperation')} />
                    </SelectTrigger>
                    <SelectContent>
                      {filterForm.field && getOperationsForField(filterForm.field).map(op => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Valore - su desktop va nella stessa riga */}
              <div className="flex-1 min-w-[200px] space-y-2 sm:block hidden">
                <label className="font-medium">{t('ui.filterDropdown.value')}</label>
                {isDateField(filterForm.field) ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal rounded-md hover:bg-gray-100",
                          !dateRange.from && !dateRange.to && "text-muted-foreground"
                        )}
                        disabled={!filterForm.field}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {dateRange.from ? (
                          dateRange.to ? (
                            <>
                              {formatDate(dateRange.from)} -{" "}
                              {formatDate(dateRange.to)}
                            </>
                          ) : (
                            formatDate(dateRange.from)
                          )
                        ) : (
                          t('ui.filterDropdown.pickDateRange')
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="range"
                        selected={dateRange}
                        onSelect={(range) => {
                          setDateRange(range);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Input
                    value={filterForm.value}
                    onChange={(e) => setFilterForm({ ...filterForm, value: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder={t('ui.enterValue')}
                    disabled={!filterForm.operation}
                  />
                )}
              </div>

              {/* Bottone Add - su desktop va nella stessa riga */}
              <div className="space-y-2 flex-shrink-0 sm:block hidden">
                <label className="font-medium opacity-0">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('ui.filterDropdown.add')}</label>
                <Button
                  onClick={handleAddFilter}
                  disabled={
                    !filterForm.field || 
                    (isDateField(filterForm.field) 
                      ? (!dateRange.from && !dateRange.to) 
                      : (!filterForm.operation || !filterForm.value)
                    )
                  }
                  className="h-10 px-4"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('ui.filterDropdown.add')}
                </Button>
              </div>
            </div>

            {/* Mobile: Seconda riga - Valore e Bottone Add */}
            <div className="flex flex-wrap items-end gap-3 w-full sm:hidden">
              {/* Valore */}
              <div className="flex-1 min-w-[200px] space-y-2">
                <label className="font-medium">{t('ui.filterDropdown.value')}</label>
                {isDateField(filterForm.field) ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal rounded-md hover:bg-gray-100",
                          !dateRange.from && !dateRange.to && "text-muted-foreground"
                        )}
                        disabled={!filterForm.field}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {dateRange.from ? (
                          dateRange.to ? (
                            <>
                              {formatDate(dateRange.from)} -{" "}
                              {formatDate(dateRange.to)}
                            </>
                          ) : (
                            formatDate(dateRange.from)
                          )
                        ) : (
                          t('ui.filterDropdown.pickDateRange')
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="range"
                        selected={dateRange}
                        onSelect={(range) => {
                          setDateRange(range);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Input
                    value={filterForm.value}
                    onChange={(e) => setFilterForm({ ...filterForm, value: e.target.value })}
                    onKeyPress={handleKeyPress}
                    placeholder={t('ui.enterValue')}
                    disabled={!filterForm.operation}
                  />
                )}
              </div>

              {/* Bottone Aggiungi */}
              <div className="space-y-2 flex-shrink-0">
                <label className="font-medium opacity-0">
                  <Plus className="h-4 w-4 mr-1" />
                  {t('ui.filterDropdown.add')}</label>
                <Button
                  onClick={handleAddFilter}
                  disabled={
                    !filterForm.field || 
                    (isDateField(filterForm.field) 
                      ? (!dateRange.from && !dateRange.to) 
                      : (!filterForm.operation || !filterForm.value)
                    )
                  }
                  className="h-10 px-4"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t('ui.filterDropdown.add')}
                </Button>
              </div>
            </div>
          </div>

          {/* Filtri Attivi */}
          {filters.length > 0 && (
            <div className="space-y-2">
              <label className="font-medium">{t('ui.filterDropdown.activeFilters')}</label>
              <div className="space-y-2">
                {filters.map((filter, index) => {
                  const fieldOption = options.find(opt => opt.value === filter.field);
                  const operationOption = fieldOption?.operations.find(op => op.value === filter.operation);

                  return (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="">
                        <b>{fieldOption?.label}</b>
                        {isDateField(filter.field) && filter.dateRange ? (
                          <span>
                            {" "}{t('ui.filterDropdown.from')}{" "}
                            <b>{filter.dateRange.from ? formatDate(filter.dateRange.from) : t('ui.filterDropdown.any')}</b>
                            {" "}{t('ui.filterDropdown.to')}{" "}
                            <b>{filter.dateRange.to ? formatDate(filter.dateRange.to) : t('ui.filterDropdown.any')}</b>
                          </span>
                        ) : (
                          <span>
                            {" "}{operationOption?.label} <b>{filter.value}</b>
                          </span>
                        )}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFilter(index)}
                        className="h-8 w-8 p-0"
                        aria-label={t('ui.filterDropdown.remove')}
                      >
                        ×
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
